import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Page } from "./_shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Store, MapPin, Plus, Pencil, Trash2, ArrowLeftRight, Package, Eye } from "lucide-react";
import { toast } from "sonner";

const SECTORS = [
  "Varejo geral", "Moda", "Alimentos/Mercearia", "Eletrônicos",
  "Farmácia", "Casa/Decoração", "Beleza/Cosméticos", "Esportes",
  "Livraria", "Brinquedos", "Outros",
];

const EMPTY = {
  name: "", city: "", state: "", cnpj: "", sector: "",
  address: "", phone: "", is_primary: false, is_active: true,
};

export default function Stores() {
  const [stores, setStores] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | store
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [stockView, setStockView] = useState(null);

  const load = useCallback(
    () => api.get("/stores").then((r) => setStores(r.data)),
    []
  );
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (s) => {
    setForm({
      name: s.name || "", city: s.city || "", state: s.state || "",
      cnpj: s.cnpj || "", sector: s.sector || "", address: s.address || "",
      phone: s.phone || "",
      is_primary: !!s.is_primary,
      is_active: s.is_active !== false,
    });
    setEditing(s);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Informe o nome da loja");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/stores", form);
        toast.success("Loja criada");
      } else {
        await api.patch(`/stores/${editing.id}`, form);
        toast.success("Loja atualizada");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/stores/${toDelete.id}`);
      toast.success("Loja excluída");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  if (!stores) return <Skeleton className="h-72" />;

  return (
    <Page
      title="Gestão multi-loja"
      subtitle="Cadastre suas unidades, acompanhe a saúde de cada uma e transfira estoque entre lojas."
      action={
        <div className="flex flex-wrap gap-2">
          {stores.length >= 2 && (
            <Button
              data-testid="open-transfer-btn"
              onClick={() => setTransferOpen(true)}
              variant="outline"
              className="border-primary/40 text-primary hover:bg-primary/10 touch-manipulation"
            >
              <ArrowLeftRight className="w-4 h-4 mr-2" /> Transferir estoque
            </Button>
          )}
          <Button
            data-testid="store-new-btn"
            onClick={openNew}
            className="bg-primary hover:bg-primary/90 touch-manipulation"
          >
            <Plus className="w-4 h-4 mr-2" /> Nova loja
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {stores.map((s) => {
          const score = s.health.score;
          const grade = s.health.grade;
          const gradeColor = grade === "A" ? "text-emerald-400" : grade === "B" ? "text-primary" : "text-orange-400";
          return (
            <div
              key={s.id}
              data-testid={`store-${s.id}`}
              className={`p-6 rounded-xl border bg-card hover:border-primary/40 transition ${s.is_active === false ? "opacity-60" : "border-border"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Store className="w-4 h-4 text-primary shrink-0" />
                    <h3 className="font-display text-lg font-semibold truncate">{s.name}</h3>
                    {s.is_primary && <span className="text-xs px-2 py-0.5 rounded-md bg-primary/15 text-primary border border-primary/30">Matriz</span>}
                    {s.is_active === false && <span className="text-xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground border border-border">Inativa</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                    {(s.city || s.state) && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[s.city, s.state].filter(Boolean).join(" / ")}
                      </div>
                    )}
                    {s.cnpj && <div>CNPJ: {formatCnpj(s.cnpj)}</div>}
                    {s.sector && <div>Setor: {s.sector}</div>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-3xl font-bold text-primary">{score}</div>
                  <div className={`text-sm font-display font-bold ${gradeColor}`}>grade {grade}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-6">
                <Mini label="Produtos" value={s.product_count} />
                <Mini label="Risco ruptura" value={s.health.stockout_count} accent />
                <Mini label="Parados" value={s.health.idle_count} />
              </div>
              <div className="flex items-center gap-2 mt-5 pt-5 border-t border-border">
                <Button
                  data-testid={`store-view-${s.id}`}
                  onClick={() => setStockView(s)}
                  variant="outline"
                  size="sm"
                  className="flex-1 h-10 touch-manipulation"
                >
                  <Eye className="w-3.5 h-3.5 mr-2" /> Ver estoque
                </Button>
                <Button
                  data-testid={`store-edit-${s.id}`}
                  onClick={() => openEdit(s)}
                  variant="outline"
                  size="sm"
                  className="flex-1 h-10 touch-manipulation"
                >
                  <Pencil className="w-3.5 h-3.5 mr-2" /> Editar
                </Button>
                <Button
                  data-testid={`store-delete-${s.id}`}
                  onClick={() => setToDelete(s)}
                  variant="outline"
                  size="sm"
                  className="h-10 px-3 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 touch-manipulation"
                  disabled={stores.length <= 1}
                  title={stores.length <= 1 ? "Você precisa de pelo menos uma loja" : ""}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
        {stores.length === 0 && (
          <div className="md:col-span-2 p-10 rounded-xl border border-dashed border-border text-center text-muted-foreground">
            <Store className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" />
            <p>Nenhuma loja cadastrada.</p>
            <Button onClick={openNew} className="mt-4 bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" /> Criar primeira loja
            </Button>
          </div>
        )}
      </div>

      <StoreFormDialog
        editing={editing}
        form={form}
        setForm={setForm}
        saving={saving}
        onSave={save}
        onClose={() => setEditing(null)}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="store-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir loja "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Produtos e vendas vinculados serão transferidos para a loja matriz.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="store-delete-confirm"
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-500/90"
            >
              Excluir loja
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TransferDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        stores={stores}
        onSuccess={load}
      />

      <StockViewDialog
        store={stockView}
        onClose={() => setStockView(null)}
      />
    </Page>
  );
}

// ---------- Sub-components ----------

function StoreFormDialog({ editing, form, setForm, saving, onSave, onClose }) {
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="store-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing === "new" ? "Nova loja" : "Editar loja"}</DialogTitle>
          <DialogDescription>
            Cadastre todos os dados da unidade. Os dados de CNPJ, setor e endereço são usados pela IA
            para gerar previsões mais precisas e benchmarking por região/segmento.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSave} className="space-y-4">
          <div>
            <Label htmlFor="name">Nome da loja *</Label>
            <Input
              data-testid="store-name-input"
              id="name" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex: Loja Centro"
              className="mt-2"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cnpj">CNPJ</Label>
              <Input
                data-testid="store-cnpj-input"
                id="cnpj" value={form.cnpj}
                onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                placeholder="Apenas números"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="sector">Setor</Label>
              <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
                <SelectTrigger data-testid="store-sector-select" id="sector" className="mt-2">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {SECTORS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="address">Endereço completo</Label>
            <Input
              data-testid="store-address-input"
              id="address" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Rua, número, bairro, CEP"
              className="mt-2"
            />
          </div>
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-3">
              <Label htmlFor="city">Cidade</Label>
              <Input
                data-testid="store-city-input"
                id="city" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="São Paulo"
                className="mt-2"
              />
            </div>
            <div className="col-span-1">
              <Label htmlFor="state">UF</Label>
              <Input
                data-testid="store-state-input"
                id="state" value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="SP" maxLength={2}
                className="mt-2"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="phone">Telefone</Label>
              <Input
                data-testid="store-phone-input"
                id="phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="11 99999-9999"
                className="mt-2"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-3 cursor-pointer select-none p-3 rounded-lg border border-border hover:border-primary/40 touch-manipulation">
              <Checkbox
                data-testid="store-primary-check"
                checked={form.is_primary}
                onCheckedChange={(v) => setForm({ ...form, is_primary: !!v })}
              />
              <div>
                <div className="text-sm font-medium">Loja matriz</div>
                <div className="text-xs text-muted-foreground">Recebe produtos órfãos.</div>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer select-none p-3 rounded-lg border border-border hover:border-primary/40 touch-manipulation">
              <Checkbox
                data-testid="store-active-check"
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: !!v })}
              />
              <div>
                <div className="text-sm font-medium">Loja ativa</div>
                <div className="text-xs text-muted-foreground">Inclusa nos relatórios.</div>
              </div>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button
              data-testid="store-save-btn"
              type="submit"
              disabled={saving}
              className="bg-primary hover:bg-primary/90 touch-manipulation"
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ open, onClose, stores, onSuccess }) {
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setFromId(""); setToId(""); setProductId("");
      setQuantity(""); setNote(""); setProducts([]);
    }
  }, [open]);

  useEffect(() => {
    if (!fromId) { setProducts([]); return; }
    api.get(`/stores/${fromId}/stock`).then((r) => {
      setProducts((r.data.products || []).filter((p) => p.stock > 0));
    });
  }, [fromId]);

  const submit = async (e) => {
    e.preventDefault();
    const qty = parseInt(quantity, 10);
    if (!fromId || !toId || !productId || !qty || qty <= 0) {
      toast.error("Preencha origem, destino, produto e quantidade");
      return;
    }
    if (fromId === toId) {
      toast.error("Origem e destino devem ser diferentes");
      return;
    }
    setSaving(true);
    try {
      await api.post("/stores/transfer", {
        product_id: productId,
        from_store_id: fromId,
        to_store_id: toId,
        quantity: qty,
        note,
      });
      toast.success("Transferência registrada");
      onClose();
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao transferir");
    } finally {
      setSaving(false);
    }
  };

  const selectedProduct = products.find((p) => p.id === productId);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="transfer-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Transferir estoque entre lojas</DialogTitle>
          <DialogDescription>
            Movimente unidades de um produto de uma loja para outra. O estoque é decrementado na origem e somado no destino.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="from">Origem *</Label>
              <Select value={fromId} onValueChange={(v) => { setFromId(v); setProductId(""); }}>
                <SelectTrigger data-testid="transfer-from-select" id="from" className="mt-2">
                  <SelectValue placeholder="Loja origem" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="to">Destino *</Label>
              <Select value={toId} onValueChange={setToId}>
                <SelectTrigger data-testid="transfer-to-select" id="to" className="mt-2">
                  <SelectValue placeholder="Loja destino" />
                </SelectTrigger>
                <SelectContent>
                  {stores.filter((s) => s.id !== fromId).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="product">Produto *</Label>
            <Select value={productId} onValueChange={setProductId} disabled={!fromId}>
              <SelectTrigger data-testid="transfer-product-select" id="product" className="mt-2">
                <SelectValue placeholder={fromId ? "Escolha o produto" : "Selecione origem primeiro"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} — {p.stock} em estoque
                  </SelectItem>
                ))}
                {fromId && products.length === 0 && (
                  <div className="p-3 text-sm text-muted-foreground">Sem produtos com estoque nesta loja.</div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="qty">Quantidade * {selectedProduct ? `(máx ${selectedProduct.stock})` : ""}</Label>
            <Input
              data-testid="transfer-quantity-input"
              id="qty" type="number" min="1"
              max={selectedProduct?.stock || undefined}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0"
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="note">Observação (opcional)</Label>
            <Input
              data-testid="transfer-note-input"
              id="note" value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex: Reposição mensal"
              className="mt-2"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button
              data-testid="transfer-submit-btn"
              type="submit" disabled={saving}
              className="bg-primary hover:bg-primary/90 touch-manipulation"
            >
              <ArrowLeftRight className="w-4 h-4 mr-2" />
              {saving ? "Transferindo..." : "Transferir"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StockViewDialog({ store, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!store) { setData(null); return; }
    setData(null);
    api.get(`/stores/${store.id}/stock`).then((r) => setData(r.data));
  }, [store]);

  const summary = useMemo(() => {
    if (!data) return null;
    const items = data.products || [];
    return {
      count: items.length,
      totalUnits: items.reduce((a, p) => a + (p.stock || 0), 0),
      totalValue: data.total_stock_value || 0,
    };
  }, [data]);

  return (
    <Dialog open={!!store} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="stock-view-dialog" className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Estoque — {store?.name}</DialogTitle>
          <DialogDescription>
            Todos os produtos vinculados a esta loja.
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <Skeleton className="h-64" />
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <Mini label="Produtos" value={summary?.count ?? 0} />
              <Mini label="Unidades totais" value={summary?.totalUnits ?? 0} />
              <Mini label="Valor estoque" value={`R$ ${(summary?.totalValue ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} accent />
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Produto</th>
                    <th className="px-3 py-2 text-left font-medium">SKU</th>
                    <th className="px-3 py-2 text-right font-medium">Estoque</th>
                    <th className="px-3 py-2 text-right font-medium">Preço</th>
                  </tr>
                </thead>
                <tbody>
                  {data.products.length === 0 && (
                    <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">
                      <Package className="w-6 h-6 mx-auto mb-2 opacity-50" />
                      Nenhum produto nesta loja
                    </td></tr>
                  )}
                  {data.products.map((p) => (
                    <tr key={p.id} className="border-t border-border hover:bg-secondary/20">
                      <td className="px-3 py-2 truncate max-w-xs">{p.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{p.sku}</td>
                      <td className={`px-3 py-2 text-right font-medium ${p.stock === 0 ? "text-red-400" : ""}`}>
                        {p.stock}
                      </td>
                      <td className="px-3 py-2 text-right">R$ {Number(p.price).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Mini = ({ label, value, accent }) => (
  <div className="p-3 rounded-lg border border-border bg-secondary/40">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`font-display text-xl font-bold mt-1 ${accent ? "text-primary" : ""}`}>{value}</div>
  </div>
);

function formatCnpj(d) {
  const v = (d || "").replace(/\D/g, "");
  if (v.length === 14)
    return v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  if (v.length === 11)
    return v.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  return v;
}
